import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, Home as HomeIcon, TrendingUp, Key, Building2, Search, Globe2, Film, ShieldCheck, Star, Quote, Sun } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { PropertyCard } from "../components/PropertyCard";
import { img } from "../lib/utils-app";
import { HeroSearch } from "../components/SearchBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const MND_LOGO = "/mnd-logo.png";
const TAB_IMAGES = {
  buy: "https://images.unsplash.com/photo-1753194931766-211c9f563156?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  rent: "https://images.unsplash.com/photo-1702830499141-a0634d87d6af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sell: "https://images.unsplash.com/photo-1782024743263-bb153ea077e1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  invest: "https://images.unsplash.com/photo-1535498730771-e735b998cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};
const HERO_VIDEOS = ["/hero.mp4"];
const HERO_POSTER = "/hero-poster.jpg";

// Hero loop: scene 0 = M&D brand intro (animated logo reveal), then the hero
// video plays, then it cycles back to the brand intro. If the video ever fails
// to load/play, we fall back to looping ONLY the logo-reveal intro.
function HeroCarousel({ onScene }) {
  const [active, setActive] = useState(0);
  const [videoOk, setVideoOk] = useState(true);
  const refs = useRef([]);
  const advance = () => setActive((a) => (a + 1) % (videoOk ? HERO_VIDEOS.length + 1 : 1));

  useEffect(() => { onScene?.(active); }, [active, onScene]);

  useEffect(() => {
    if (active === 0) {
      const id = setTimeout(advance, 5200);
      return () => clearTimeout(id);
    }
    const vi = active - 1;
    refs.current.forEach((el, idx) => {
      if (!el) return;
      el.muted = true;
      if (idx === vi) { try { el.currentTime = 0; } catch {} el.play?.().catch(() => {}); }
      else el.pause?.();
    });
  }, [active]);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid="hero-slideshow">
      {/* M&D brand intro */}
      <div className={`absolute inset-0 z-[3] bg-gradient-to-br from-[#e8f6f7] via-[#fbf8f1] to-[#ffe7dc] grid place-items-center transition-opacity duration-[900ms] ${active === 0 ? "opacity-100" : "opacity-0 pointer-events-none"}`} data-testid="hero-intro">
        <div className="absolute w-[70vw] h-[70vw] max-w-[820px] max-h-[820px] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute w-[40vw] h-[40vw] max-w-[520px] max-h-[520px] rounded-full bg-accent/10 blur-[120px] translate-x-1/3 translate-y-1/3" />
        <motion.div
          key={active === 0 ? "intro-in" : "intro-out"}
          initial={{ opacity: 0, scale: 0.85 }}
          animate={active === 0 ? { opacity: 1, scale: 1 } : { opacity: 0 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex flex-col items-center px-6"
        >
          <img src={MND_LOGO} alt="M&D Realtors" className="w-[230px] md:w-[380px] logo-shimmer drop-shadow-[0_16px_46px_rgba(20,48,56,0.18)]" />
          <span className="mt-7 label-caps text-foreground/55" style={{ letterSpacing: "0.55em" }}>M&amp;D REALTORS</span>
        </motion.div>
      </div>

      {/* property videos */}
      {HERO_VIDEOS.map((src, idx) => (
        <video
          key={idx}
          ref={(el) => { refs.current[idx] = el; }}
          src={src}
          muted
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          onEnded={advance}
          onError={() => { setVideoOk(false); setActive(0); }}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-in-out ${active === idx + 1 ? "opacity-100" : "opacity-0"}`}
          data-testid={`hero-video-${idx}`}
        />
      ))}
      {/* minimal overlay — keep the video vivid, only a soft bottom blend into the page */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-background/70 via-transparent to-transparent" />
    </div>
  );
}

// Compact tabbed intent bar sitting just below the hero.
function HomeTabs() {
  const { t, lang } = useLang();
  const intents = [
    { key: "buy", to: "/buy", icon: HomeIcon, label: t("lifestyle.buyer"), desc: t("lifestyle.buyerDesc"),
      image: TAB_IMAGES.buy, stat: lang === "es" ? "120+ propiedades activas" : "120+ active listings",
      chips: [
        { label: lang === "es" ? "Frente al mar" : "Waterfront", to: "/buy?q=waterfront" },
        { label: lang === "es" ? "Bajo $1M" : "Under $1M", to: "/buy?max_price=1000000" },
        { label: "Villas", to: "/buy?property_type=villa" },
      ] },
    { key: "rent", to: "/rent", icon: Building2, label: t("lifestyle.renter"), desc: t("lifestyle.renterDesc"),
      image: TAB_IMAGES.rent, stat: lang === "es" ? "Contratos flexibles" : "Flexible lease terms",
      chips: [
        { label: lang === "es" ? "Condominios" : "Condos", to: "/rent?property_type=condo" },
        { label: lang === "es" ? "2+ Habitaciones" : "2+ Beds", to: "/rent?bedrooms=2" },
        { label: lang === "es" ? "Apartamentos" : "Apartments", to: "/rent?property_type=apartment" },
      ] },
    { key: "sell", to: "/sell", icon: Key, label: t("lifestyle.seller"), desc: t("lifestyle.sellerDesc"),
      image: TAB_IMAGES.sell, stat: lang === "es" ? "Vendemos en ~30 días" : "Sold in ~30 days",
      chips: [
        { label: lang === "es" ? "Tasación gratis" : "Free valuation", to: "/sell" },
        { label: lang === "es" ? "Fotografía pro" : "Pro photography", to: "/sell" },
        { label: lang === "es" ? "Alcance global" : "Global reach", to: "/sell" },
      ] },
    { key: "invest", to: "/buy?property_type=commercial", icon: TrendingUp, label: t("lifestyle.investor"), desc: t("lifestyle.investorDesc"),
      image: TAB_IMAGES.invest, stat: lang === "es" ? "Mercados prime de Florida" : "Prime Florida markets",
      chips: [
        { label: lang === "es" ? "Comercial" : "Commercial", to: "/buy?property_type=commercial" },
        { label: lang === "es" ? "Frente al mar" : "Waterfront", to: "/buy?q=waterfront" },
        { label: lang === "es" ? "Bajo $2M" : "Under $2M", to: "/buy?max_price=2000000" },
      ] },
  ];
  return (
    <section className="relative z-20 max-w-[1200px] mx-auto px-5 lg:px-10 -mt-28 md:-mt-32" data-testid="home-tabs">
      <div className="mb-5 flex justify-center" data-testid="home-search-wrap">
        <HeroSearch />
      </div>
      <div className="bg-card/90 backdrop-blur-2xl border border-primary/15 shadow-2xl shadow-primary/10">
        <div className="h-1 w-full bg-gradient-to-r from-primary via-accent to-gold" />
        <Tabs defaultValue="buy" className="w-full">
          <TabsList className="w-full grid grid-cols-2 sm:grid-cols-4 bg-transparent h-auto p-0 rounded-none border-b border-primary/10">
            {intents.map((it) => (
              <TabsTrigger
                key={it.key}
                value={it.key}
                data-testid={`home-tab-${it.key}`}
                className="rounded-none py-5 flex items-center justify-center gap-2 label-caps text-muted-foreground border-b-2 border-transparent transition-all duration-300 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none"
              >
                <it.icon size={16} /> {it.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {intents.map((it) => (
            <TabsContent key={it.key} value={it.key} className="mt-0 p-6 md:p-8 grid md:grid-cols-2 gap-8 items-center" data-testid={`home-tab-panel-${it.key}`}>
              <div>
                <h3 className="font-serif text-3xl md:text-4xl font-light text-foreground">{it.label}</h3>
                <p className="text-muted-foreground font-light mt-3 leading-relaxed">{it.desc}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {it.chips.map((c) => (
                    <Link key={c.label} to={c.to} data-testid={`home-chip-${it.key}`} className="label-caps text-[0.58rem] px-3 py-1.5 rounded-full bg-accent/10 text-accent border border-accent/20 hover:bg-accent hover:text-accent-foreground transition-colors duration-300">{c.label}</Link>
                  ))}
                </div>
                <Link to={it.to} data-testid={`home-tab-cta-${it.key}`} className="btn-lux inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-3.5 mt-7 label-caps hover:brightness-110">
                  {it.label} <ArrowRight size={16} className="btn-arrow" />
                </Link>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden rounded-lg hover-zoom border border-primary/10">
                <img src={it.image} alt={it.label} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a2c33]/70 via-transparent to-transparent" />
                <span className="absolute bottom-4 left-4 label-caps text-white text-[0.62rem] flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent" /> {it.stat}
                </span>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
}

function Reveal({ children, className = "", delay = 0 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, delay }} className={className}>
      {children}
    </motion.div>
  );
}

function SectionLabel({ children }) {
  return (
    <span className="inline-flex items-center gap-2 label-caps text-primary mb-4">
      <span className="w-8 h-px bg-brandgreen" />{children}
    </span>
  );
}

const HERO_PHRASES = {
  en: [
    { tag: "Contemporary Luxury Real Estate", title: "Find where your next chapter begins", sub: "Curated luxury homes, waterfront estates, and investment properties across South Florida." },
    { tag: "South Florida Living", title: "Wake up to the water, every day", sub: "Waterfront villas, private docks, and sunset skylines — coastal living, redefined." },
    { tag: "M&D Realtors", title: "Your home. Our expertise.", sub: "Guided by advisors who understand extraordinary living." },
    { tag: "Invest with Confidence", title: "Own your piece of paradise", sub: "Prime Florida markets, timeless architecture, and lasting value." },
  ],
  es: [
    { tag: "Bienes Raíces de Lujo", title: "Encuentra dónde comienza tu próximo capítulo", sub: "Casas de lujo, propiedades frente al mar e inversiones en todo el sur de Florida." },
    { tag: "Vida en el Sur de Florida", title: "Despierta frente al mar, cada día", sub: "Villas frente al mar, muelles privados y atardeceres — la vida costera, redefinida." },
    { tag: "M&D Realtors", title: "Tu hogar. Nuestra experiencia.", sub: "Guiado por asesores que entienden la vida extraordinaria." },
    { tag: "Invierte con Confianza", title: "Adquiere tu pedazo de paraíso", sub: "Mercados prime de Florida, arquitectura atemporal y valor duradero." },
  ],
};

// Rotating hero copy — fades in, holds, then fades fully out (brief pure-video
// gap via AnimatePresence mode="wait") before the next phrase.
function HeroText({ visible }) {
  const { lang } = useLang();
  const phrases = HERO_PHRASES[lang] || HERO_PHRASES.en;
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % phrases.length), 6000);
    return () => clearInterval(id);
  }, [phrases.length]);
  const p = phrases[i];
  return (
    <div className="relative z-10 max-w-[1440px] mx-auto px-5 lg:px-10 w-full min-h-[320px] flex items-start" data-testid="hero-rotating-text">
      <AnimatePresence mode="wait">
        {visible && (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 label-caps text-white mb-5 hero-text-shadow">
              <span className="w-10 h-px bg-brandgreen" />{p.tag}
            </span>
            <h1 className="font-serif text-6xl md:text-7xl lg:text-[6.5rem] font-light leading-[1.0] tracking-tight max-w-5xl text-white hero-text-shadow">
              {p.title}
            </h1>
            <p className="mt-7 text-xl md:text-2xl font-light text-white/90 max-w-2xl leading-relaxed hero-text-shadow">
              {p.sub}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Home() {
  const { t, lang, L } = useLang();
  const [featured, setFeatured] = useState([]);
  const [anyListing, setAnyListing] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [scene, setScene] = useState(0);

  useEffect(() => {
    api.get("/properties?featured=true&limit=6").then((r) => setFeatured(r.data.items));
    api.get("/properties?limit=1").then((r) => setAnyListing(r.data.items?.[0] || null)).catch(() => {});
    api.get("/neighborhoods").then((r) => setNeighborhoods(r.data.items));
    api.get("/articles").then((r) => setArticles(r.data.items.slice(0, 3)));
    api.get("/testimonials").then((r) => setTestimonials(r.data.items));
  }, []);

  const lifestyles = [
    { icon: HomeIcon, title: t("lifestyle.buyer"), desc: t("lifestyle.buyerDesc"), to: "/buy",
      img: "https://images.pexels.com/photos/6538930/pexels-photo-6538930.jpeg?auto=compress&cs=tinysrgb&w=1000&h=1200&dpr=2" },
    { icon: Key, title: t("lifestyle.seller"), desc: t("lifestyle.sellerDesc"), to: "/sell",
      img: "https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg?auto=compress&cs=tinysrgb&w=1000&h=1200&dpr=2" },
    { icon: Building2, title: t("lifestyle.renter"), desc: t("lifestyle.renterDesc"), to: "/rent",
      img: "https://images.pexels.com/photos/7546548/pexels-photo-7546548.jpeg?auto=compress&cs=tinysrgb&w=1000&h=1200&dpr=2" },
    { icon: TrendingUp, title: t("lifestyle.investor"), desc: t("lifestyle.investorDesc"), to: "/buy?property_type=commercial",
      img: "https://images.pexels.com/photos/31546910/pexels-photo-31546910.jpeg?auto=compress&cs=tinysrgb&w=1000&h=1200&dpr=2" },
  ];
  const stats = [
    { v: "1,200+", l: t("stats.properties") }, { v: "$4.8B", l: t("stats.sales") },
    { v: "6,500+", l: t("stats.clients") }, { v: "45", l: t("stats.agents") },
  ];
  const bestListing = featured.find((p) => (p.images || []).length) || anyListing;
  const whyImg = bestListing?.images?.[0] ? img(bestListing.images[0]) : "/hero-poster.jpg";
  const why = [
    { icon: Search, title: t("why.f1"), desc: t("why.f1d") },
    { icon: Globe2, title: t("why.f2"), desc: t("why.f2d") },
    { icon: Film, title: t("why.f3"), desc: t("why.f3d") },
    { icon: ShieldCheck, title: t("why.f4"), desc: t("why.f4d") },
  ];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center">
        <HeroCarousel onScene={setScene} />
        <HeroText visible={scene !== 0} />
      </section>

      {/* INTENT TABS */}
      <HomeTabs />

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <Reveal>
            <SectionLabel>{t("home.featuredTag")}</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{t("home.featuredTitle")}</h2>
            <p className="text-muted-foreground font-light mt-3 max-w-xl">{t("home.featuredSub")}</p>
          </Reveal>
          <Link to="/buy" className="group flex items-center gap-2 label-caps text-primary hover:gap-3 transition-all duration-300" data-testid="view-all-featured">
            {t("common.viewAll")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
        </div>
      </section>

      {/* LIFESTYLE */}
      <section className="border-y border-border bg-secondary/40">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-20">
          <Reveal className="mb-12">
            <SectionLabel>{t("home.exploreTag")}</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{t("home.exploreTitle")}</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {lifestyles.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.08}>
                <Link to={l.to} className="group relative block h-full min-h-[360px] overflow-hidden hover-zoom" data-testid={`lifestyle-${i}`}>
                  <img src={l.img} alt={l.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 scrim" />
                  <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors duration-500" />
                  <div className="relative z-10 h-full flex flex-col justify-between p-7">
                    <span className="w-12 h-12 grid place-items-center bg-white/10 backdrop-blur-md border border-white/30 text-white">
                      <l.icon size={22} strokeWidth={1.4} />
                    </span>
                    <div>
                      <h3 className="font-serif text-3xl text-white leading-tight">{l.title}</h3>
                      <p className="text-sm text-white/80 font-light mt-1">{l.desc}</p>
                      <span className="inline-flex items-center gap-2 mt-4 label-caps text-white group-hover:gap-3 transition-all duration-300">
                        {t("common.learnMore")} <ArrowUpRight size={16} />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <Reveal className="mb-12 text-center max-w-2xl mx-auto">
          <SectionLabel>{t("home.neighborhoodsTag")}</SectionLabel>
          <h2 className="font-serif text-4xl md:text-5xl font-light">{t("home.neighborhoodsTitle")}</h2>
          <p className="text-muted-foreground font-light mt-3">{t("home.neighborhoodsSub")}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {neighborhoods.map((n, i) => (
            <Reveal key={n.id} delay={i * 0.06}>
              <Link to={`/neighborhoods/${n.slug}`} className="group relative block aspect-[3/4] hover-zoom" data-testid={`neighborhood-${n.slug}`}>
                <img src={n.image} alt={n.name} className="w-full h-full object-cover" />
                <div className="absolute inset-0 scrim" />
                <div className="absolute bottom-0 p-6">
                  <p className="label-caps text-primary mb-1">{n.city}</p>
                  <h3 className="font-serif text-2xl text-white">{n.name}</h3>
                  <p className="text-white/70 text-sm mt-1">{n.stats?.listings} {t("search.results")}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-border bg-primary/[0.04]">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="text-center">
              <p className="font-serif text-5xl md:text-6xl text-primary font-light">{s.v}</p>
              <p className="label-caps text-muted-foreground mt-2">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-[1440px] mx-auto px-5 lg:px-10 py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden hover-zoom">
              <img src={whyImg} alt="" className="w-full h-full object-cover" data-testid="why-featured-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="font-serif text-6xl text-white leading-none">15+</p>
                <p className="label-caps text-white/90 mt-2">{lang === "es" ? "Años de excelencia" : "Years of excellence"}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal className="mb-10">
              <SectionLabel>{t("home.whyTag")}</SectionLabel>
              <h2 className="font-serif text-4xl md:text-5xl font-light">{t("home.whyTitle")}</h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {why.map((w, i) => (
                <Reveal key={i} delay={i * 0.08} className="border-t border-brandgreen/50 pt-5">
                  <w.icon size={28} className="text-primary mb-3" strokeWidth={1.2} />
                  <h3 className="font-serif text-2xl mb-2">{w.title}</h3>
                  <p className="text-sm text-muted-foreground font-light leading-relaxed">{w.desc}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-y border-border bg-card/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
          <Reveal className="mb-12 text-center">
            <SectionLabel>{t("home.testimonialsTitle")}</SectionLabel>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((tm, i) => (
              <Reveal key={tm.id} delay={i * 0.08}>
                <div className="bg-background border border-border p-8 h-full flex flex-col">
                  <Quote size={28} className="text-primary/50 mb-4" />
                  <p className="font-serif text-xl font-light leading-relaxed flex-1">"{L(tm.quote)}"</p>
                  <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
                    <img src={tm.photo} alt={tm.name} className="w-11 h-11 rounded-full object-cover" />
                    <div>
                      <p className="font-medium text-sm">{tm.name}</p>
                      <p className="text-xs text-muted-foreground">{L(tm.role)}</p>
                    </div>
                    <div className="ml-auto flex gap-0.5">
                      {Array.from({ length: tm.rating }).map((_, j) => <Star key={j} size={13} className="fill-primary text-primary" />)}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <Reveal>
            <SectionLabel>{t("home.journalTag")}</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-light">{t("home.journalTitle")}</h2>
          </Reveal>
          <Link to="/journal" className="group flex items-center gap-2 label-caps text-primary" data-testid="view-all-journal">
            {t("common.viewAll")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {articles.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.08}>
              <Link to={`/journal/${a.slug}`} className="group block" data-testid={`article-${a.slug}`}>
                <div className="aspect-[16/10] hover-zoom mb-4"><img src={a.image} alt="" className="w-full h-full object-cover" /></div>
                <p className="label-caps text-primary mb-2">{a.category}</p>
                <h3 className="font-serif text-2xl group-hover:text-primary transition-colors duration-300">{L(a.title)}</h3>
                <p className="text-sm text-muted-foreground font-light mt-2 line-clamp-2">{L(a.excerpt)}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden">
        <img src="https://images.pexels.com/photos/31737860/pexels-photo-31737860.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&dpr=2" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <Reveal className="relative z-10 max-w-3xl mx-auto text-center px-5">
          <h2 className="font-serif text-4xl md:text-6xl font-light text-white">{t("home.ctaTitle")}</h2>
          <p className="text-white/80 font-light mt-4 text-lg">{t("home.ctaSub")}</p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link to="/contact" className="px-8 py-4 bg-primary text-primary-foreground label-caps hover:brightness-110 transition-[filter] duration-300" data-testid="cta-contact">{t("nav.contact")}</Link>
            <Link to="/buy" className="px-8 py-4 border border-white/40 text-white label-caps hover:bg-white hover:text-black transition-colors duration-300" data-testid="cta-browse">{t("nav.buy")}</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
